/**
 * The corrected monotonic clock (D9).
 *
 * The countdown is the product, so it is never driven by the browser's wall
 * clock — which the user can change, which drifts, and which a laptop resets on
 * wake. This is the **only** file in the client permitted to read `Date.now()`,
 * and it reads it exactly twice: to stamp an outgoing `/time` sample, and to
 * anchor the monotonic base against the estimated offset. Everything rendered
 * is `performance.now()` plus that base.
 *
 * Three corrections feed it, in descending cost and ascending frequency:
 *
 * 1. **The handshake** — 10 serial `GET /time` samples, filtered, median
 *    offset. Cold load, tab wake, reconnect, and the periodic backstop.
 * 2. **The pong sampler** — the ping the client sends every 5 s to keep the
 *    socket alive comes back carrying `client_time_ms` echoed and
 *    `recv_time_ms`, which `asyncapi` annotates as *"`t2` in the NTP
 *    4-timestamp set (D9)"*. Together with the pong's own `server_time_ms` and
 *    our receive stamp that is the **same four timestamps `GET /time` produces**
 *    — an unbiased offset sample every 5 seconds, over a socket that is already
 *    open, for no extra request. This is the mechanism that actually keeps the
 *    clock honest between handshakes.
 * 3. **The residual EWMA** — every REST payload and every WS frame carries
 *    `server_time_ms`, which is free but **biased late by the one-way latency**:
 *    the frame was stamped before it travelled to us, so every such hint pulls
 *    the estimate backwards, and averaging thousands of them does not help
 *    because they all err the same way. `α` is small for exactly that reason.
 *    It is a floor under drift, not a source of truth — and being *behind* is
 *    the dangerous direction, since it makes the client believe there is more
 *    time left than there is.
 *
 * Slew governs all three: a correction under 250 ms is applied at no more than
 * 50 ms per second, so the rendered countdown never runs backwards. Over 250 ms
 * it snaps, and the UI says "re-synced" rather than pretending nothing happened.
 *
 * The slew is lazy: it advances as a function of elapsed monotonic time inside
 * `now()`, so there is no timer, no drift between the timer and the render, and
 * nothing to get out of step when the tab is throttled.
 */

import {
  DRIFT_SNAP_THRESHOLD_MS,
  MAX_SLEW_RATE_MS_PER_S,
  estimateOffset,
  type OffsetEstimate,
  type TimeSample,
} from './offset'

export type ClockStatus =
  /** No sync has been attempted. Render a dash, not a countdown. */
  | 'UNSYNCED'
  /** A sync ran and failed its sample floor. Running at θ = 0, behind the
   *  "clock unverified" indicator. */
  | 'UNVERIFIED'
  | 'SYNCED'

export interface ClockOptions {
  /** Injected for tests. The two wall-clock reads this module is allowed. */
  wallNow?: () => number
  /** Injected for tests. Monotonic; immune to the user changing their clock. */
  monoNow?: () => number
  snapThresholdMs?: number
  maxSlewRateMsPerS?: number
  /** EWMA weight on the free per-payload residual. */
  ewmaAlpha?: number
  /** Pong samples retained for the rolling median. At a 5 s ping that is ~40 s. */
  pongWindow?: number
  /** Pong samples older than this are dropped — a sample taken before a network
   *  change describes a path that no longer exists. */
  pongMaxAgeMs?: number
}

/** Which mechanism produced the estimate. Worth surfacing: an offset the client
 *  has only ever had from the biased path is not the same claim as one measured
 *  over a bounded round trip. */
export type ClockSyncSource = 'HANDSHAKE' | 'PONG'

export interface SyncResult {
  status: ClockStatus
  estimate: OffsetEstimate | null
  /** How far this sync moved the clock. */
  correctionMs: number
  /** `true` when the correction exceeded the threshold and was applied at once. */
  snapped: boolean
  source: ClockSyncSource
}

/** One ping/pong round trip, as the frame carries it. */
export interface PongTimestamps {
  /** The value `stampPing()` returned, echoed back in `PongData.client_time_ms`. */
  clientTimeMs: number
  /** `PongData.recv_time_ms`. Optional in the schema; absent means the server's
   *  two stamps coincide, exactly as for a `GET /time` sample. */
  serverRecvMs?: number | undefined
  /** `server_time_ms` on the pong frame itself — present on every server frame. */
  serverTimeMs: number
}

export const DEFAULT_EWMA_ALPHA = 0.05
/** Eight samples at a 5 s ping is ~40 s of history — enough for a stable median,
 *  short enough to follow a real change in the path. */
export const DEFAULT_PONG_WINDOW = 8
export const DEFAULT_PONG_MAX_AGE_MS = 120_000

export class Clock {
  readonly #wallNow: () => number
  readonly #monoNow: () => number
  readonly #snapThresholdMs: number
  readonly #maxSlewRateMsPerS: number
  readonly #alpha: number

  /** `serverEstimate = monoNow() + base`. The applied value. */
  #appliedBase = 0
  /** Where `#appliedBase` is slewing to. */
  #targetBase = 0
  /** Monotonic reading at which the slew was last advanced. */
  #slewMarkMs = 0

  #status: ClockStatus = 'UNSYNCED'
  #measuredOffsetMs = 0
  #rttMs: number | null = null
  #ewmaResidualMs = 0
  #lastSyncAtMs: number | null = null
  #lastSnapAtMs: number | null = null
  #lastSyncSource: ClockSyncSource | null = null

  readonly #pongWindowSize: number
  readonly #pongMaxAgeMs: number
  /** Rolling window of ping/pong samples, newest last, each tagged with the
   *  monotonic reading at which it arrived so it can be aged out. */
  #pongSamples: Array<{ sample: TimeSample; monoAt: number }> = []

  constructor(options: ClockOptions = {}) {
    this.#wallNow = options.wallNow ?? (() => Date.now())
    this.#monoNow = options.monoNow ?? (() => performance.now())
    this.#snapThresholdMs = options.snapThresholdMs ?? DRIFT_SNAP_THRESHOLD_MS
    this.#maxSlewRateMsPerS = options.maxSlewRateMsPerS ?? MAX_SLEW_RATE_MS_PER_S
    this.#alpha = options.ewmaAlpha ?? DEFAULT_EWMA_ALPHA
    this.#pongWindowSize = options.pongWindow ?? DEFAULT_PONG_WINDOW
    this.#pongMaxAgeMs = options.pongMaxAgeMs ?? DEFAULT_PONG_MAX_AGE_MS
    // Until the first sync, the estimate is the wall clock. It is wrong by
    // whatever the offset turns out to be, and `status` says so.
    const mono = this.#monoNow()
    this.#appliedBase = this.#wallNow() - mono
    this.#targetBase = this.#appliedBase
    this.#slewMarkMs = mono
  }

  get status(): ClockStatus {
    return this.#status
  }

  /** `|θ|` as last measured against the wall clock. Above 250 ms the ticket
   *  soft-disables one-click entry for the final 500 ms (D9, docs/07 §1). */
  get measuredOffsetMs(): number {
    return this.#measuredOffsetMs
  }

  get offsetExceedsTolerance(): boolean {
    return Math.abs(this.#measuredOffsetMs) > DRIFT_SNAP_THRESHOLD_MS
  }

  get rttMs(): number | null {
    return this.#rttMs
  }

  /** Server-clock reading of the last successful sync. `null` before the first. */
  get lastSyncAtMs(): number | null {
    return this.#lastSyncAtMs
  }

  get lastSnapAtMs(): number | null {
    return this.#lastSnapAtMs
  }

  /** Correction still to be slewed in. Non-zero means the clock is mid-adjustment. */
  get pendingCorrectionMs(): number {
    this.#advanceSlew()
    return this.#targetBase - this.#appliedBase
  }

  /**
   * `t_server_estimate`. Every rendered timestamp in the client is a difference
   * of this and an absolute `*_at_ms` the server published — never a duration
   * the client accumulated itself.
   */
  now(): number {
    this.#advanceSlew()
    return this.#monoNow() + this.#appliedBase
  }

  /** `expiry_at_ms − t_server_estimate`, clamped at zero. The countdown, in one call. */
  remainingMs(atMs: number): number {
    return Math.max(0, atMs - this.now())
  }

  /** Signed, for the cases that care which side of the boundary they are on. */
  untilMs(atMs: number): number {
    return atMs - this.now()
  }

  /**
   * Apply a completed handshake.
   *
   * The first successful sync always snaps: there is no prior estimate worth
   * preserving, and slewing in from the wall clock would show a visibly wrong
   * countdown for as long as the error divided by 50 ms/s.
   */
  applySync(samples: readonly TimeSample[]): SyncResult {
    const estimate = estimateOffset(samples)
    if (!estimate) {
      // Run at θ = 0 rather than at a number three samples could not agree on.
      this.#status = 'UNVERIFIED'
      this.#measuredOffsetMs = 0
      this.#rttMs = null
      return { status: this.#status, estimate: null, correctionMs: 0, snapped: false, source: 'HANDSHAKE' }
    }
    return this.#applyEstimate(estimate, 'HANDSHAKE')
  }

  // -------------------------------------------------------------------------
  // The pong sampler
  // -------------------------------------------------------------------------

  /**
   * Stamp an outgoing `ping`. The returned value goes on the frame as
   * `client_time_ms` and comes back echoed on the `pong`, which is what makes
   * the round trip self-correlating — no pending-request table, exactly as
   * `GET /time` echoes `client_sent_ms`.
   *
   * It exists so the socket never has to read the wall clock itself: `Date.now()`
   * stays inside this module (D9).
   */
  stampPing(): number {
    return this.#wallNow()
  }

  /**
   * Feed a completed ping/pong round trip.
   *
   * This is the correction that matters. The four timestamps are the same set
   * `GET /time` yields, so the offset is measured **net of the round trip**
   * rather than inferred from a timestamp that was already stale when it
   * arrived — and it costs nothing, because the ping is sent every 5 s anyway to
   * keep the socket alive.
   *
   * Samples accumulate into a rolling window and go through the same filter and
   * median as a handshake, because one sample is one path measurement and the
   * whole point of D9 is not to trust a single one. Returns `null` until the
   * window reaches the sample floor.
   *
   * `rtt_hint_ms` on the frame is deliberately ignored: `asyncapi` marks it
   * "purely diagnostic; the client's own median-of-samples offset is
   * authoritative".
   *
   * A frame handed to this method must **not** also be handed to
   * `observeServerTime` — that would apply the biased hint on top of the
   * unbiased measurement.
   */
  observePong(pong: PongTimestamps): SyncResult | null {
    const t3 = this.#wallNow()
    const sample: TimeSample = {
      t0: pong.clientTimeMs,
      // Absent `recv_time_ms` means the server's two stamps coincide.
      t1: pong.serverRecvMs ?? pong.serverTimeMs,
      t2: pong.serverTimeMs,
      t3,
    }

    const mono = this.#monoNow()
    this.#pongSamples.push({ sample, monoAt: mono })
    this.#prunePongSamples(mono)

    const estimate = estimateOffset(this.#pongSamples.map((s) => s.sample))
    if (!estimate) return null
    return this.#applyEstimate(estimate, 'PONG')
  }

  /**
   * Drop the pong window.
   *
   * Called on reconnect and on network change: samples taken over the previous
   * path describe a route that no longer exists, and a median over a mix of two
   * paths is a number neither of them would produce.
   */
  resetPongSamples(): void {
    this.#pongSamples = []
  }

  get pongSampleCount(): number {
    return this.#pongSamples.length
  }

  /** Which mechanism last moved the clock. `null` before any sync succeeded. */
  get lastSyncSource(): ClockSyncSource | null {
    return this.#lastSyncSource
  }

  #prunePongSamples(monoNow: number): void {
    const oldest = monoNow - this.#pongMaxAgeMs
    while (this.#pongSamples.length > 0 && (this.#pongSamples[0] as { monoAt: number }).monoAt < oldest) {
      this.#pongSamples.shift()
    }
    while (this.#pongSamples.length > this.#pongWindowSize) this.#pongSamples.shift()
  }

  /**
   * Apply a filtered, median offset from either source.
   *
   * The first successful sync always snaps: there is no prior estimate worth
   * preserving, and slewing in from the wall clock would show a visibly wrong
   * countdown for as long as the error divided by 50 ms/s.
   */
  #applyEstimate(estimate: OffsetEstimate, source: ClockSyncSource): SyncResult {
    this.#advanceSlew()
    const mono = this.#monoNow()
    const newBase = this.#wallNow() + estimate.offsetMs - mono
    const correction = newBase - this.#appliedBase
    const first = this.#status !== 'SYNCED'
    const snapped = first || Math.abs(correction) > this.#snapThresholdMs

    this.#targetBase = newBase
    if (snapped) {
      this.#appliedBase = newBase
      this.#lastSnapAtMs = mono + newBase
    }
    this.#slewMarkMs = mono

    this.#status = 'SYNCED'
    // Refreshing this is half the point of sampling often: it is what
    // `offsetExceedsTolerance` reads, and that flag gates D9's final-500 ms
    // soft-disable. Measured only every 15 minutes, the flag is decorative.
    this.#measuredOffsetMs = estimate.offsetMs
    this.#rttMs = estimate.rttMs
    this.#ewmaResidualMs = 0
    this.#lastSyncAtMs = mono + this.#appliedBase
    this.#lastSyncSource = source

    return { status: this.#status, estimate, correctionMs: correction, snapped, source }
  }

  /**
   * The free correction: every REST payload and every WS frame carries
   * `server_time_ms`.
   *
   * The residual is biased late by roughly the one-way latency, which is why
   * `α = 0.05` and why this never sets `status` to `SYNCED` on its own — it
   * catches slow drift between handshakes, it does not stand in for one.
   */
  observeServerTime(serverTimeMs: number): void {
    if (!Number.isFinite(serverTimeMs)) return
    if (this.#status === 'UNSYNCED') return

    const residual = serverTimeMs - this.now()
    this.#ewmaResidualMs = this.#ewmaResidualMs + this.#alpha * (residual - this.#ewmaResidualMs)

    // Target the applied base plus the smoothed residual. As the slew closes
    // the gap, later residuals shrink, the EWMA decays toward zero, and the
    // target converges back onto the applied base rather than running away.
    this.#targetBase = this.#appliedBase + this.#ewmaResidualMs

    if (Math.abs(this.#ewmaResidualMs) > this.#snapThresholdMs) {
      this.#appliedBase = this.#targetBase
      this.#slewMarkMs = this.#monoNow()
      this.#lastSnapAtMs = this.now()
      this.#ewmaResidualMs = 0
    }
  }

  /** Discard the estimate — used when a tab has been hidden long enough that
   *  the sensible thing is to say so and re-sync, not to keep counting. */
  invalidate(): void {
    this.#status = this.#status === 'UNSYNCED' ? 'UNSYNCED' : 'UNVERIFIED'
  }

  #advanceSlew(): void {
    const gap = this.#targetBase - this.#appliedBase
    const mono = this.#monoNow()
    if (gap === 0) {
      this.#slewMarkMs = mono
      return
    }
    const elapsed = mono - this.#slewMarkMs
    this.#slewMarkMs = mono
    if (elapsed <= 0) return

    const step = (this.#maxSlewRateMsPerS * elapsed) / 1000
    this.#appliedBase += Math.abs(gap) <= step ? gap : Math.sign(gap) * step
  }
}
